'use client';

import { useAuth } from '@clerk/nextjs';
import {
  FAVOURITE_BOATS_LIMIT,
  isAtFavouriteLimit,
  useAddFavoriteMutation,
  useFavoritesQuery,
  useRemoveFavoriteMutation,
} from '@marine/services';
import { message } from 'antd';
import { useCallback } from 'react';

/**
 * The favorite state of one public boat, for the card and button that render it.
 *
 * Every caller shares a single `GET /favorites` — the query key names the kind of favoritable and
 * nothing else, so React Query collapses a page of twenty cards into one request. That is why this
 * is a per-boat hook rather than something a list has to thread down through props.
 *
 * `showToggle` is the signed-in check. A signed-out visitor is not shown a control that could only
 * fail, and `useFavoritesQuery` is disabled for them, so a boat list page issues no favorites
 * request at all until someone signs in.
 */
export const useBoatFavorite = (sanityBoatId: string) => {
  const { isSignedIn } = useAuth();

  const {
    data: favorites,
    isFavorite,
    favoriteUuidOf,
    isPending: isLoadingFavorites,
  } = useFavoritesQuery({ enabled: !!isSignedIn });

  const { mutate: addFavorite, isPending: isAdding } = useAddFavoriteMutation();
  const { mutate: removeFavorite, isPending: isRemoving } = useRemoveFavoriteMutation();

  const isFavorited = isFavorite(sanityBoatId);

  // Counted from the same cached list the toggle reads, so the guard and the heart can never
  // disagree about how many boats are saved.
  const isAtLimit = isAtFavouriteLimit(favorites?.length ?? 0);

  const toggle = useCallback(() => {
    const favoriteUuid = favoriteUuidOf(sanityBoatId);

    // Decided from the cached list rather than from a local flag: the list is what the optimistic
    // update writes, so the two can never disagree about which way this click goes.
    if (favoriteUuid) {
      removeFavorite({ uuid: favoriteUuid });

      return;
    }

    // Only adding is capped. Removing at the limit is how a user gets back under it, so it is the
    // one thing that must never be blocked here.
    if (isAtLimit) {
      message.warning(
        `You can save up to ${FAVOURITE_BOATS_LIMIT} boats. Remove one from your favourites to make room for this.`,
      );

      return;
    }

    addFavorite({ favoritableRef: sanityBoatId, uuid: crypto.randomUUID() });
  }, [addFavorite, favoriteUuidOf, isAtLimit, removeFavorite, sanityBoatId]);

  return {
    showToggle: !!isSignedIn,
    isFavorited,
    /**
     * Only the in-flight writes, not the list read. The mutations update the cache before they are
     * sent, so disabling on `isLoadingFavorites` too would grey out the button on first paint for
     * no reason a user could see.
     */
    isToggling: isAdding || isRemoving,
    isLoadingFavorites: !!isSignedIn && isLoadingFavorites,
    /**
     * True once the account is full. The heart stays enabled — the click is what explains the
     * limit, and a boat that is already favorited still has to be removable.
     */
    isAtLimit,
    toggle,
  };
};

export default useBoatFavorite;
